import {
  autoUpdate,
  flip,
  FloatingFocusManager,
  FloatingPortal,
  offset,
  shift,
  size,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import type { ReactNode } from 'react';

/** What the trigger must spread onto its `<button>`. */
export type TriggerProps = Record<string, unknown> & {
  ref: (node: HTMLElement | null) => void;
};

export interface PopoverProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The panel's accessible name. */
  label: string;
  /** Renders the button that opens the panel, with `props` spread onto it. */
  trigger: (props: TriggerProps) => ReactNode;
  children: ReactNode;
  /** Which of its button's edges it lines up with: a menu at the right ends there. */
  align?: 'start' | 'end';
}

/**
 * A panel that opens below its button: placed by Floating UI, closed by Escape or a click
 * outside — not one in a dialog opened from it, which stands over it — focus returned to the button on close (docs/02 §6). The content decides where focus
 * starts — the calendar puts it on the chosen day.
 */
export function Popover({
  open,
  onOpenChange,
  label,
  trigger,
  children,
  align = 'start',
}: PopoverProps) {
  const { refs, floatingStyles, context } = useFloating({
    open,
    onOpenChange,
    placement: `bottom-${align}`,
    // Fixed, it is on the screen from the first frame: focus moved into it before it is placed
    // would otherwise scroll the page to its top.
    strategy: 'fixed',
    middleware: [
      offset(6),
      flip(),
      shift({ padding: 8 }),
      // Never past the screen's edge: a long one scrolls within itself.
      size({
        padding: 8,
        apply({ availableHeight, elements }) {
          elements.floating.style.maxHeight = `${String(Math.max(160, availableHeight))}px`;
        },
      }),
    ],
    whileElementsMounted: autoUpdate,
  });
  const { getReferenceProps, getFloatingProps } = useInteractions([
    useClick(context),
    // A dialog opened from it stands over it, the panel waiting under it: a press there is
    // not outside. One in a dialog it was opened in still closes when the dialog is pressed.
    useDismiss(context, {
      outsidePress: (event) => {
        const over =
          event.target instanceof Element && event.target.closest('.dialog-backdrop');
        return !over || over.contains(refs.domReference.current);
      },
    }),
    useRole(context, { role: 'dialog' }),
  ]);

  return (
    <>
      {trigger({
        ref: (node) => {
          refs.setReference(node);
        },
        ...getReferenceProps(),
      })}
      {open && (
        <FloatingPortal>
          <FloatingFocusManager
            context={context}
            modal={false}
            initialFocus={-1}
            closeOnFocusOut={false}
          >
            <div
              ref={(node) => {
                refs.setFloating(node);
              }}
              className="popover"
              style={floatingStyles}
              aria-label={label}
              {...getFloatingProps()}
            >
              {children}
            </div>
          </FloatingFocusManager>
        </FloatingPortal>
      )}
    </>
  );
}
